import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PencilIcon, PlusIcon, ChevronUpIcon, ChevronDownIcon, TrashIcon, PencilSquareIcon, PhotoIcon, XMarkIcon, ArrowDownTrayIcon, CheckIcon, XCircleIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import UserAvatar from '../../UserAvatar';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import client from '../../../lib/feathers';
import ClipboardButton from '../../ClipboardButton';
import PassportAnalyticsPanel from './Analytics/PassportAnalyticsPanel';
import { formatAddressDisplay } from '../../../lib/addressUtils';
import { formatDateOnly } from '../../../lib/dateOnly';

import putToStorage from '../../../lib/putToStorage';
const DEFAULT_OPT_IN_COPY = 'By participating in this passport, you consent to share your contact information (name, email, check-in date) with the venues you visit during the passport period.';

export default function IndustryPassportDetail() {
  const { pid, lid } = useParams();
  const currentListingId = lid;
  const [passport, setPassport] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [updating, setUpdating] = useState(false);
  const [showAddListingModal, setShowAddListingModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showEditListingModal, setShowEditListingModal] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [editFormData, setEditFormData] = useState({ subtitle: '', description: '' });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [organizers, setOrganizers] = useState([]);
  const [loadingOrganizers, setLoadingOrganizers] = useState(false);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [togglingMembership, setTogglingMembership] = useState(null); // userId being toggled
  const [participantFilter, setParticipantFilter] = useState('all'); // 'all' | 'completed'
  const logoInputRef = useRef(null);

  const publicUrl = `${process.env.REACT_APP_CLIENT_URL}/passport/${pid}`;

  const loadQrCode = async () => {
    try {
      const service = client.service('industry/qr-code');
      const code = await service.create({ url: publicUrl });
      setQrCode(code);
    } catch (e) {
      console.error('Error generating QR code:', e);
    }
  };

  const loadPassport = async () => {
    try {
      setLoading(true);
      const service = client.service('industry/passport');
      const result = await service.get(pid);
      setPassport(result);
    } catch (e) {
      console.error('Error loading passport:', e);
      setError('Failed to load passport');
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async (listingId) => {
    if (!listingId) return;
    try {
      const service = client.service('industry/passport-contacts');
      const result = await service.find({
        query: {
          passportId: pid,
          listingId,
        },
      });
      setContacts(result.data || []);
    } catch (e) {
      console.error('Error loading contacts:', e);
    }
  };

  const loadParticipants = async () => {
    try {
      const service = client.service('industry/passport-participants');
      const result = await service.find({
        query: { passportId: pid },
      });
      setParticipants(result.data || []);
    } catch (e) {
      console.error('Error loading participants:', e);
    }
  };

  const loadOrganizers = async () => {
    try {
      setLoadingOrganizers(true);
      const service = client.service('industry/passport-organizer');
      const result = await service.find({
        query: { passportId: pid },
      });
      setOrganizers(result.data || []);
    } catch (e) {
      console.error('Error loading organizers:', e);
    } finally {
      setLoadingOrganizers(false);
    }
  };

  const handleRequestOrganizerAccess = async () => {
    if (!window.confirm('Request organizer access to this passport? Current organizers will be notified.')) {
      return;
    }
    try {
      setRequestingAccess(true);
      const service = client.service('industry/passport-organizer-request');
      await service.create({ passportId: pid });
      // Reload passport to get updated organizerStatus
      await loadPassport();
      alert('Organizer access requested successfully. Current organizers have been notified.');
    } catch (e) {
      console.error('Error requesting access:', e);
      alert(e.message || 'Failed to request organizer access');
    } finally {
      setRequestingAccess(false);
    }
  };

  const handleUpdateOrganizerStatus = async (organizerId, newStatus) => {
    const statusActions = {
      active: 'approve',
      denied: 'deny',
      revoked: 'revoke',
    };
    const action = statusActions[newStatus] || newStatus;

    if (!window.confirm(`Are you sure you want to ${action} this organizer?`)) {
      return;
    }

    try {
      setUpdating(true);
      const service = client.service('industry/passport-organizer');
      await service.patch(organizerId, { status: newStatus });
      loadOrganizers();
    } catch (e) {
      console.error('Error updating organizer status:', e);
      alert(e.message || 'Failed to update organizer status');
    } finally {
      setUpdating(false);
    }
  };

  const exportParticipants = async () => {
    try {
      const service = client.service('industry/passport-participants');
      const result = await service.get(pid);

      // Download CSV
      const blob = new Blob([result.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `passport-${passport.title.replace(/\s+/g, '-')}-participants.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error exporting participants:', e);
      alert('Failed to export participants');
    }
  };

  const handleAcceptParticipation = async (listingPassportId) => {
    try {
      setUpdating(true);
      const service = client.service('industry/listing-passport');
      await service.patch(listingPassportId, { accepted: true });
      loadPassport();
    } catch (e) {
      console.error('Error accepting:', e);
      alert('Failed to accept participation');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeclineParticipation = async (listingPassportId) => {
    if (!window.confirm('Are you sure you want to decline participation?')) return;
    try {
      setUpdating(true);
      const service = client.service('industry/listing-passport');
      await service.patch(listingPassportId, { accepted: false });
      loadPassport();
    } catch (e) {
      console.error('Error declining:', e);
      alert('Failed to decline participation');
    } finally {
      setUpdating(false);
    }
  };

  const exportContacts = async () => {
    try {
      const service = client.service('industry/passport-contacts');
      const result = await service.get(pid, {
        query: { listingId: currentListingId },
      });

      const blob = new Blob([result.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `passport-contacts-${passport.title.replace(/\s+/g, '-')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error exporting:', e);
      alert('Failed to export contacts');
    }
  };

  const handleToggleMembership = async (contact) => {
    const newMemberStatus = !contact.joinedClubDuringPassport;
    const action = newMemberStatus
      ? 'mark as signed up for membership during this passport'
      : 'remove the membership signup record';

    if (!window.confirm(`Are you sure you want to ${action} for ${contact.firstName} ${contact.lastName}?`)) {
      return;
    }

    setTogglingMembership(contact.id);
    try {
      // Find the passportUser record for this contact
      const passportUserService = client.service('industry/passport-contacts');
      // We need to get the passportUser ID - it's not in the contact data, so we need to look it up
      // Actually, we need to use the patch endpoint with the contact's user ID
      // The contacts API returns user data, but we need to find their passportUser record
      const passportUsersResult = await client.service('industry/passport-participants').find({
        query: {
          passportId: pid,
          $limit: 10000,
        },
      });
      const passportUser = passportUsersResult.data?.find((pu) => pu.userId === contact.id);

      if (!passportUser) {
        throw new Error('Could not find passport user record');
      }

      await passportUserService.patch(passportUser.id, {
        listingId: currentListingId,
        isMember: newMemberStatus,
      });

      // Refresh contacts list
      await loadContacts(currentListingId);
    } catch (e) {
      console.error('Error toggling membership:', e);
      alert(e.message || 'Failed to update membership status');
    } finally {
      setTogglingMembership(null);
    }
  };

  useEffect(() => {
    loadPassport();
    loadQrCode();
  }, [pid]);

  // Load organizers when passport is loaded and user is organizer (for badge count)
  useEffect(() => {
    if (passport?.isOrganizer) {
      loadOrganizers();
    }
  }, [passport?.isOrganizer]);

  useEffect(() => {
    if (activeTab === 'contacts' && currentListingId) {
      loadContacts(currentListingId);
    }
    if (activeTab === 'participants' && passport?.isOrganizer) {
      loadParticipants();
    }
    if (activeTab === 'organizers' && passport?.isOrganizer) {
      loadOrganizers();
    }
  }, [activeTab, currentListingId, passport?.isOrganizer]);

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      ended: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleSearchListings = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      // Use the client/listing service with name search (filtered to wineries only)
      const service = client.service('client/listing');
      const result = await service.find({
        query: {
          name: { $iLike: `%${query}%` },
          $limit: 30 // Fetch more to account for filtering
        }
      });
      // Filter out listings already in the passport, keeping wine venues
      // (winery / vineyard / tasting room / wine bar / wine shop — not just
      // "Winery", which hid vineyards like Potter's Vineyard).
      const wineVenue = new Set(['Winery', 'Vineyard', 'Tasting Room', 'Wine Bar', 'Wine Shop']);
      const existingListingIds = passport.allListings?.map((l) => l.listingId) || [];
      const filtered = (result.data || result || []).filter(
        (l) => !existingListingIds.includes(l.id)
          && l.categories?.some((c) => wineVenue.has(c.name))
      );
      setSearchResults(filtered.slice(0, 10));
    } catch (e) {
      console.error('Error searching listings:', e);
    } finally {
      setSearching(false);
    }
  };

  const handleAddListing = async (listingId) => {
    try {
      setUpdating(true);
      const service = client.service('industry/listing-passport');
      await service.create({
        passportId: pid,
        listingId
      });
      setShowAddListingModal(false);
      setSearchQuery('');
      setSearchResults([]);
      loadPassport();
    } catch (e) {
      console.error('Error adding listing:', e);
      alert(e.message || 'Failed to add listing');
    } finally {
      setUpdating(false);
    }
  };

  const handleReorderListing = async (listingPassportId, currentOrder, direction) => {
    const newOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;
    if (newOrder < 0) return;

    try {
      setUpdating(true);
      const service = client.service('industry/listing-passport');

      // Find the listing that currently has the target order
      const swapListing = passport.allListings.find((l) => l.order === newOrder);
      if (swapListing) {
        // Swap orders
        await service.patch(swapListing.listingPassportId, { order: currentOrder });
      }
      await service.patch(listingPassportId, { order: newOrder });

      loadPassport();
    } catch (e) {
      console.error('Error reordering:', e);
      alert(e.message || 'Failed to reorder listing');
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveListing = async (listingPassportId, listingName) => {
    if (!window.confirm(`Are you sure you want to remove "${listingName}" from this passport?`)) {
      return;
    }
    try {
      setUpdating(true);
      const service = client.service('industry/listing-passport');
      await service.remove(listingPassportId);
      loadPassport();
    } catch (e) {
      console.error('Error removing listing:', e);
      alert(e.message || 'Failed to remove listing');
    } finally {
      setUpdating(false);
    }
  };

  const handleEditListing = (listing) => {
    setEditingListing(listing);
    setEditFormData({
      subtitle: listing.subtitle || '',
      description: listing.description || '',
    });
    // Set logo preview if listing has a logo
    if (listing.logo?.path) {
      setLogoPreview(`${process.env.REACT_APP_ATTACHMENT_URL}/${listing.logo.path}?w=300&f=inside`);
    } else {
      setLogoPreview(null);
    }
    setLogoFile(null);
    setShowEditListingModal(true);
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async (listingPassportId, listingId) => {
    if (!logoFile) return;

    setUploadingLogo(true);
    try {
      // Step 1: Get signed URL
      const uploadService = client.service('industry/listing-passport-logo-upload');
      const { signedRequest, path } = await uploadService.create({
        passportId: pid,
        listingId,
        'file-type': logoFile.type
      });

      // Step 2: Upload to S3
      await putToStorage(signedRequest, logoFile);

      // Step 3: Persist the attachment
      await uploadService.patch(listingPassportId, {
        path,
        mime: logoFile.type
      });

      return true;
    } catch (e) {
      console.error('Error uploading logo:', e);
      throw e;
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!editingListing) return;

    // If there's no existing logo (just a preview from file selection), just clear it
    if (!editingListing.logo) {
      setLogoFile(null);
      setLogoPreview(null);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
      return;
    }

    if (!window.confirm('Are you sure you want to remove the custom logo?')) return;

    setUpdating(true);
    try {
      const uploadService = client.service('industry/listing-passport-logo-upload');
      await uploadService.remove(editingListing.listingPassportId);
      setLogoFile(null);
      setLogoPreview(null);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
      // Update the local state
      setEditingListing({ ...editingListing, logo: null });
      loadPassport();
    } catch (e) {
      console.error('Error removing logo:', e);
      alert(e.message || 'Failed to remove logo');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveListingEdit = async () => {
    if (!editingListing) return;
    setUpdating(true);
    try {
      // Save subtitle and description
      await client.service('industry/listing-passport').patch(editingListing.listingPassportId, {
        subtitle: editFormData.subtitle || null,
        description: editFormData.description || null,
      });

      // Upload logo if a new file was selected
      if (logoFile) {
        await uploadLogo(editingListing.listingPassportId, editingListing.listingId);
      }

      setShowEditListingModal(false);
      setEditingListing(null);
      setLogoFile(null);
      setLogoPreview(null);
      loadPassport();
    } catch (e) {
      console.error('Error saving listing:', e);
      alert(e.message || 'Failed to save changes');
    } finally {
      setUpdating(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearchListings(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-icon"></div>
      </div>
    );
  }

  if (error || !passport) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">{error || 'Passport not found'}</p>
        <Link to={`/industry/listings/${lid}/passports`} className="mt-2 text-icon hover:underline">
          Back to Passports
        </Link>
      </div>
    );
  }

  // Get listing IDs the user owns
  const myListingIds = passport.myListings?.map((l) => l.listingId) || [];

  // Count pending organizer requests for badge
  const pendingOrganizerCount = organizers.filter((o) => o.status === 'pending').length;

  // Group tabs: "My Listing" tabs vs "Manage Passport" tabs (organizer only)
  const myListingTabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'contacts', label: 'Contacts' },
    { id: 'analytics', label: 'Analytics', icon: ChartBarIcon },
  ];
  const manageTabs = passport.isOrganizer ? [
    { id: 'listings', label: 'Listings' },
    { id: 'participants', label: 'Participants' },
    { id: 'organizers', label: 'Organizers', badge: pendingOrganizerCount },
  ] : [];

  return (
    <div>
      <div className="pb-5 border-b border-gray-200">
        <Link to={`/industry/listings/${lid}/passports`} className="text-sm text-gray-500 hover:text-gray-700">
          &larr; Back to Passports
        </Link>
        <div className="mt-2 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{passport.title}</h1>
              {passport.isOrganizer && (
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                  Organizer
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {formatDateOnly(passport.startDate)}
              {' '}
              -
              {' '}
              {formatDateOnly(passport.endDate)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(passport.status)}`}>
              {passport.status}
            </span>

            {/* Request Organizer Access button - show for non-organizers with listings in passport */}
            {!passport.isOrganizer && passport.myListings?.length > 0 && (
              <>
                {passport.organizerStatus === 'pending' && (
                  <span className="inline-flex items-center px-3 py-2 border border-yellow-300 text-sm font-medium rounded-md text-yellow-700 bg-yellow-50">
                    Access Requested
                  </span>
                )}
                {passport.organizerStatus === 'denied' && (
                  <span className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50">
                    Request Denied
                  </span>
                )}
                {passport.organizerStatus === 'revoked' && (
                  <span className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-gray-50">
                    Access Revoked
                  </span>
                )}
                {!passport.organizerStatus && (
                  <button
                    type="button"
                    onClick={handleRequestOrganizerAccess}
                    disabled={requestingAccess}
                    className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50"
                  >
                    {requestingAccess ? 'Requesting...' : 'Request Organizer Access'}
                  </button>
                )}
              </>
            )}

            {passport.isOrganizer && (
              <Link
                to={`/industry/listings/${lid}/passports/${pid}/edit`}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Cover Image and QR Code Section */}
      {(passport.coverImage || passport.description) && (
        <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <div className="md:grid md:grid-cols-3 md:gap-6">
              {/* Left Column: Cover Image and Description */}
              <div className="md:col-span-2">
                {passport.coverImage && (
                  <div style={{ maxWidth: 375 }}>
                    <img
                      src={`${process.env.REACT_APP_ATTACHMENT_URL}/${passport.coverImage.path}`}
                      alt="Passport cover"
                      className="w-full object-cover rounded-lg"
                    />
                  </div>
                )}

                {passport.description && (
                  <div className="mt-3 text-sm text-gray-500" style={{ maxWidth: 375 }}>
                    <Markdown rehypePlugins={[rehypeRaw]} className="[&_a]:underline [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4">
                      {passport.description}
                    </Markdown>
                  </div>
                )}
              </div>

              {/* Right Column: QR Code and Public Link */}
              <div className="mt-5 md:mt-0 flex flex-col items-center">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Public QR Code</h4>
                {qrCode ? (
                  <div className="flex flex-col items-center w-full max-w-[224px]">
                    <div className="bg-white p-4 border border-gray-200 rounded-lg w-full">
                      <img src={qrCode} alt="Passport QR Code" className="w-full aspect-square" />
                    </div>
                    <a
                      href={qrCode}
                      download={`${passport.title.replace(/\s+/g, '_')}_qrcode.png`}
                      className="mt-3 inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-icon bg-white hover:bg-icon hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-icon"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                      Download QR Code
                    </a>
                  </div>
                ) : (
                  <div className="w-full max-w-[224px] aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-icon"></div>
                  </div>
                )}

                {/* Public Link */}
                <div className="mt-4 text-center">
                  <p className="text-xs text-gray-500 mb-2">Public Link</p>
                  <div className="flex items-center gap-2">
                    <a
                      href={publicUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-icon hover:text-red-700 underline break-all"
                    >
                      View Passport
                    </a>
                    <ClipboardButton text={publicUrl} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code only section (when no cover image or description) */}
      {!passport.coverImage && !passport.description && (
        <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <div className="flex flex-col items-center">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Public QR Code</h4>
              {qrCode ? (
                <div className="flex flex-col items-center w-full max-w-[224px]">
                  <div className="bg-white p-4 border border-gray-200 rounded-lg w-full">
                    <img src={qrCode} alt="Passport QR Code" className="w-full aspect-square" />
                  </div>
                  <a
                    href={qrCode}
                    download={`${passport.title.replace(/\s+/g, '_')}_qrcode.png`}
                    className="mt-3 inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-icon bg-white hover:bg-icon hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-icon"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Download QR Code
                  </a>
                </div>
              ) : (
                <div className="w-full max-w-[224px] aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-icon"></div>
                </div>
              )}

              {/* Public Link */}
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500 mb-2">Public Link</p>
                <div className="flex items-center gap-2">
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-icon hover:text-red-700 underline break-all"
                  >
                    View Passport
                  </a>
                  <ClipboardButton text={publicUrl} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mt-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex flex-wrap items-center">
            {/* My Listing tabs */}
            <div className="flex space-x-8">
              {myListingTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm relative ${
                    activeTab === tab.id
                      ? 'border-icon text-icon'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Separator and Manage tabs (organizer only) */}
            {manageTabs.length > 0 && (
              <>
                <div className="hidden sm:block mx-6 h-6 w-px bg-gray-300" />
                <div className="flex space-x-8">
                  {manageTabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm relative ${
                        activeTab === tab.id
                          ? 'border-icon text-icon'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.label}
                      {tab.badge > 0 && (
                        <span className="absolute inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-primary border-2 border-white rounded-full top-0 right">
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </nav>
        </div>

        <div className="mt-6">
          {activeTab === 'overview' && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Passport Details</h3>
              </div>
              <div className="border-t border-gray-200">
                <dl>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Completion Milestones</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {(() => {
                        const thresholds = passport.completionThreshold?.toString().split(',').map((t) => t.trim());
                        if (thresholds?.length > 1) {
                          return (
                            <div>
                              <span className="font-medium">{thresholds[0]} stops</span>
                              <span className="text-gray-500"> to complete</span>
                              <br />
                              <span className="text-gray-500 text-xs">
                                Bonus milestones: {thresholds.slice(1).join(', ')} stops
                              </span>
                            </div>
                          );
                        }
                        return `${passport.completionThreshold} stops`;
                      })()}
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Total Stops</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {passport.allListings?.length || 0} listings
                    </dd>
                  </div>

                  {/* Opt-in Message - only visible to organizers */}
                  {passport.isOrganizer && (
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Opt-In Message</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        <p className="text-gray-700">
                          {passport.optInCopy || DEFAULT_OPT_IN_COPY}
                        </p>
                        {!passport.optInCopy && (
                          <p className="mt-1 text-xs text-gray-500 italic">(Using default message)</p>
                        )}
                      </dd>
                    </div>
                  )}

                  {/* Ask Membership - only visible to organizers */}
                  {passport.isOrganizer && (
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Ask About Club Memberships</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {passport.askMembership ? (
                          <span className="text-green-600">Yes - users will be asked which clubs they belong to</span>
                        ) : (
                          <span className="text-gray-500">No</span>
                        )}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {passport.allListings && passport.allListings.length > 0 && (
                <div className="px-4 py-5 sm:px-6 border-t border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-4">All Participating Wineries</h4>
                  <ul className="divide-y divide-gray-200">
                    {passport.allListings.map((listing) => {
                      const isCurrentListing = listing.listingId === currentListingId;
                      const isMyListing = myListingIds.includes(listing.listingId);
                      const myListingData = passport.myListings?.find((l) => l.listingId === listing.listingId);

                      return (
                        <li key={listing.listingPassportId} className={`py-3 flex items-start justify-between ${isCurrentListing ? 'bg-blue-50 -mx-4 px-4 rounded' : ''}`}>
                          <div className="flex-1">
                            {listing.logo && (
                              <img
                                src={`${process.env.REACT_APP_ATTACHMENT_URL}/${listing.logo.path}?w=300&f=inside`}
                                alt={`${listing.listingName} logo`}
                                className="h-16 w-auto object-contain mb-2"
                              />
                            )}
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">{listing.listingName}</span>
                              {isCurrentListing && (
                                <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-blue-500 text-white">
                                  Viewing
                                </span>
                              )}
                            </div>
                            {listing.subtitle && (
                              <p className="text-sm text-gray-500 mt-0.5">{listing.subtitle}</p>
                            )}
                            {listing.description && (
                              <div className="text-sm text-gray-600 mt-1 prose prose-sm max-w-none">
                                <Markdown rehypePlugins={[rehypeRaw]}>{listing.description}</Markdown>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Edit button for listing owners - show if they own this listing OR if viewing from this listing */}
                            {(isMyListing || isCurrentListing) && (
                              <button
                                type="button"
                                onClick={() => handleEditListing(listing)}
                                disabled={updating}
                                className="p-1 text-gray-400 hover:text-gray-600"
                                title="Edit listing details"
                              >
                                <PencilSquareIcon className="h-4 w-4" />
                              </button>
                            )}
                            {listing.accepted ? (
                              <>
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                  Confirmed
                                </span>
                                {isCurrentListing && isMyListing && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (window.confirm(`Are you sure you want to withdraw "${listing.listingName}" from this passport?`)) {
                                        handleDeclineParticipation(myListingData?.listingPassportId || listing.listingPassportId);
                                      }
                                    }}
                                    disabled={updating}
                                    className="px-2 py-1 text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200"
                                  >
                                    Withdraw
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                  Pending
                                </span>
                                {isCurrentListing && isMyListing && (
                                  <>
                                    <button
                                      onClick={() => handleAcceptParticipation(myListingData?.listingPassportId || listing.listingPassportId)}
                                      disabled={updating}
                                      className="px-2 py-1 text-xs font-medium rounded bg-icon text-white hover:bg-red-700"
                                    >
                                      Accept
                                    </button>
                                    <button
                                      onClick={() => {
                                        if (window.confirm(`Are you sure you want to decline "${listing.listingName}" from this passport?`)) {
                                          handleDeclineParticipation(myListingData?.listingPassportId || listing.listingPassportId);
                                        }
                                      }}
                                      disabled={updating}
                                      className="px-2 py-1 text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200"
                                    >
                                      Decline
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

            </div>
          )}

          {activeTab === 'listings' && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-gray-200">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Listings
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {passport.isOrganizer
                      ? 'Add, remove, and reorder listings in this passport'
                      : 'Wineries participating in this passport'}
                  </p>
                </div>
                {passport.isOrganizer && (
                  <button
                    type="button"
                    onClick={() => setShowAddListingModal(true)}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-icon hover:bg-red-700"
                  >
                    <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                    Add Listing
                  </button>
                )}
              </div>

              {passport.allListings?.length === 0 ? (
                <div className="px-4 py-12 text-center">
                  <p className="text-sm text-gray-500">No listings in this passport yet.</p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {passport.allListings
                    ?.sort((a, b) => a.order - b.order)
                    .map((listing, index) => {
                      const isCurrentListing = listing.listingId === currentListingId;
                      const isMyListing = myListingIds.includes(listing.listingId);
                      const myListingData = passport.myListings?.find((l) => l.listingId === listing.listingId);

                      return (
                        <li key={listing.listingPassportId} className={`px-4 py-4 sm:px-6 ${isCurrentListing ? 'bg-blue-50' : ''}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                              {passport.isOrganizer && (
                                <div className="flex flex-col gap-1">
                                  <button
                                    type="button"
                                    onClick={() => handleReorderListing(listing.listingPassportId, listing.order, 'up')}
                                    disabled={index === 0 || updating}
                                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                  >
                                    <ChevronUpIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleReorderListing(listing.listingPassportId, listing.order, 'down')}
                                    disabled={index === passport.allListings.length - 1 || updating}
                                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                  >
                                    <ChevronDownIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                              <div className="flex-1">
                                {listing.logo && (
                                  <img
                                    src={`${process.env.REACT_APP_ATTACHMENT_URL}/${listing.logo.path}?w=300&f=inside`}
                                    alt={`${listing.listingName} logo`}
                                    className="h-16 w-auto object-contain mb-2"
                                  />
                                )}
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900">{listing.listingName}</span>
                                  {isCurrentListing && (
                                    <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-blue-500 text-white">
                                      Viewing
                                    </span>
                                  )}
                                </div>
                                {listing.subtitle && (
                                  <p className="mt-0.5 text-sm text-gray-500">{listing.subtitle}</p>
                                )}
                                {listing.description && (
                                  <div className="text-sm text-gray-600 mt-1 prose prose-sm max-w-none">
                                    <Markdown rehypePlugins={[rehypeRaw]}>{listing.description}</Markdown>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {/* Status badge */}
                              {listing.accepted ? (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                  Confirmed
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                  Pending
                                </span>
                              )}

                              {/* Accept button for the current listing if I own it */}
                              {isCurrentListing && isMyListing && !listing.accepted && (
                                <button
                                  onClick={() => handleAcceptParticipation(myListingData?.listingPassportId || listing.listingPassportId)}
                                  disabled={updating}
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-xs font-medium text-white bg-icon hover:bg-red-700"
                                >
                                  Accept
                                </button>
                              )}

                              {/* Edit button for organizers */}
                              {passport.isOrganizer && (
                                <button
                                  type="button"
                                  onClick={() => handleEditListing(listing)}
                                  disabled={updating}
                                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                                  title="Edit listing details"
                                >
                                  <PencilSquareIcon className="h-5 w-5" />
                                </button>
                              )}

                              {/* Remove button for organizers */}
                              {passport.isOrganizer && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveListing(listing.listingPassportId, listing.listingName)}
                                  disabled={updating}
                                  className="p-1 text-red-400 hover:text-red-600 disabled:opacity-50"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                </ul>
              )}
            </div>
          )}

          {activeTab === 'participants' && passport.isOrganizer && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      All Participants
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Users who opted in to this passport and their progress
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex rounded-md shadow-sm">
                      <button
                        type="button"
                        onClick={() => setParticipantFilter('all')}
                        className={`px-3 py-1 text-sm font-medium rounded-l-md border ${
                          participantFilter === 'all'
                            ? 'bg-icon text-white border-icon'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        All
                      </button>
                      <button
                        type="button"
                        onClick={() => setParticipantFilter('completed')}
                        className={`px-3 py-1 text-sm font-medium rounded-r-md border-t border-r border-b ${
                          participantFilter === 'completed'
                            ? 'bg-icon text-white border-icon'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Completed
                      </button>
                    </div>
                    <button
                      onClick={exportParticipants}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Export CSV
                    </button>
                  </div>
                </div>
              </div>

              {/* Desktop table */}
              <table className="hidden sm:table min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member Of</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined Club</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opted In</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(() => {
                    const filteredParticipants = participantFilter === 'completed'
                      ? participants.filter((p) => p.progress?.completed)
                      : participants;

                    if (filteredParticipants.length === 0) {
                      return (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center text-sm text-gray-500">
                            {participantFilter === 'completed'
                              ? 'No completed participants yet.'
                              : 'No participants yet. Users will appear here after they opt in to this passport.'}
                          </td>
                        </tr>
                      );
                    }

                    return filteredParticipants.map((p) => {
                      const thresholds = p.progress?.thresholds || [];
                      const milestonesReached = p.progress?.milestonesReached || 0;

                      const memberAtOptInNames = (p.memberListingIds || [])
                        .map((id) => passport.allListings?.find((lp) => lp.listingId === id)?.listingName)
                        .filter(Boolean);

                      const joinedDuringNames = (p.joinedClubListingIds || [])
                        .map((id) => passport.allListings?.find((lp) => lp.listingId === id)?.listingName)
                        .filter(Boolean);

                      return (
                        <tr key={p.id}>
                          <td className="px-6 py-4">
                            {p.user ? (
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {p.user.firstName} {p.user.lastName}
                                </div>
                                <div className="text-sm text-gray-500">{p.user.email}</div>
                              </div>
                            ) : (
                              'Unknown'
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {p.progress?.visited || 0} / {p.progress?.total || 0} stops
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              {p.progress?.completed && (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                  Completed
                                </span>
                              )}
                              {thresholds.length > 1 && (
                                <span className="px-2 inline-flex text-xs leading-5 font-medium rounded-full bg-blue-100 text-blue-800">
                                  {milestonesReached}/{thresholds.length} milestones
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {memberAtOptInNames.length > 0 ? (
                              <div className="text-sm text-gray-900">
                                {memberAtOptInNames.map((name, idx) => (
                                  <div key={idx}>{name}</div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {joinedDuringNames.length > 0 ? (
                              <div className="text-sm text-gray-900">
                                {joinedDuringNames.map((name, idx) => (
                                  <div key={idx}>{name}</div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {p.optedInAt ? new Date(p.optedInAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            }) : 'N/A'}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>

              {/* Mobile cards */}
              <div className="sm:hidden divide-y divide-gray-200">
                {(() => {
                  const filteredParticipants = participantFilter === 'completed'
                    ? participants.filter((p) => p.progress?.completed)
                    : participants;

                  if (filteredParticipants.length === 0) {
                    return (
                      <div className="px-4 py-12 text-center text-sm text-gray-500">
                        {participantFilter === 'completed'
                          ? 'No completed participants yet.'
                          : 'No participants yet. Users will appear here after they opt in to this passport.'}
                      </div>
                    );
                  }

                  return filteredParticipants.map((p) => {
                    const thresholds = p.progress?.thresholds || [];
                    const milestonesReached = p.progress?.milestonesReached || 0;

                    const memberAtOptInNames = (p.memberListingIds || [])
                      .map((id) => passport.allListings?.find((lp) => lp.listingId === id)?.listingName)
                      .filter(Boolean);

                    const joinedDuringNames = (p.joinedClubListingIds || [])
                      .map((id) => passport.allListings?.find((lp) => lp.listingId === id)?.listingName)
                      .filter(Boolean);

                    return (
                      <div key={p.id} className="px-4 py-4 space-y-2">
                        <div className="flex items-start justify-between">
                          <div>
                            {p.user ? (
                              <>
                                <div className="text-sm font-medium text-gray-900">
                                  {p.user.firstName} {p.user.lastName}
                                </div>
                                <div className="text-xs text-gray-500">{p.user.email}</div>
                              </>
                            ) : (
                              <span className="text-sm text-gray-500">Unknown</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-900">
                              {p.progress?.visited || 0}/{p.progress?.total || 0}
                            </span>
                            {p.progress?.completed && (
                              <span className="px-2 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Done
                              </span>
                            )}
                            {thresholds.length > 1 && (
                              <span className="px-2 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                {milestonesReached}/{thresholds.length}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                          {memberAtOptInNames.length > 0 && (
                            <span>Member: {memberAtOptInNames.join(', ')}</span>
                          )}
                          {joinedDuringNames.length > 0 && (
                            <span>Joined: {joinedDuringNames.join(', ')}</span>
                          )}
                          {p.optedInAt && (
                            <span>Opted in {new Date(p.optedInAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {activeTab === 'organizers' && passport.isOrganizer && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Organizers
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Manage organizer access for this passport
                </p>
              </div>

              {loadingOrganizers ? (
                <div className="px-6 py-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-icon mx-auto"></div>
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {organizers.length === 0 ? (
                    <li className="px-6 py-12 text-center text-sm text-gray-500">
                      No organizers found.
                    </li>
                  ) : (
                    organizers.map((org) => (
                      <li key={org.id} className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <UserAvatar
                              user={org.user}
                              size="md"
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {org.user?.firstName} {org.user?.lastName}
                              </p>
                              <p className="text-sm text-gray-500">{org.user?.email}</p>
                              {org.listings?.length > 0 && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                  Manages: {org.listings.map((l) => l.name).join(', ')}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* Status badge */}
                            {org.status === 'active' && (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Active
                              </span>
                            )}
                            {org.status === 'pending' && (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                Pending
                              </span>
                            )}
                            {org.status === 'denied' && (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                Denied
                              </span>
                            )}
                            {org.status === 'revoked' && (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                Revoked
                              </span>
                            )}

                            {/* Action buttons based on status */}
                            {org.status === 'pending' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleUpdateOrganizerStatus(org.id, 'active')}
                                  disabled={updating}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                >
                                  <CheckIcon className="h-3 w-3 mr-1" />
                                  Approve
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleUpdateOrganizerStatus(org.id, 'denied')}
                                  disabled={updating}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                                >
                                  <XCircleIcon className="h-3 w-3 mr-1" />
                                  Deny
                                </button>
                              </>
                            )}

                            {org.status === 'active' && (
                              <button
                                type="button"
                                onClick={() => handleUpdateOrganizerStatus(org.id, 'revoked')}
                                disabled={updating}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                              >
                                Revoke
                              </button>
                            )}

                            {(org.status === 'denied' || org.status === 'revoked') && (
                              <button
                                type="button"
                                onClick={() => handleUpdateOrganizerStatus(org.id, 'active')}
                                disabled={updating}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                              >
                                Reactivate
                              </button>
                            )}
                          </div>
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          )}

          {activeTab === 'contacts' && (() => {
            const currentListing = passport.myListings?.find((l) => l.listingId === currentListingId);
            const isAccepted = currentListing?.accepted;

            return (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Passport Contacts
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Users who opted in and checked in at your listing
                  </p>
                </div>
                {isAccepted && (
                  <button
                    onClick={exportContacts}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Export CSV
                  </button>
                )}
              </div>

              {!isAccepted ? (
                <div className="px-6 py-12 text-center border-t border-gray-200">
                  <div className="mx-auto max-w-md">
                    <p className="text-sm text-gray-600">
                      You need to accept the invitation to join this passport before you can access contacts who check in at your listing.
                    </p>
                    <p className="mt-3 text-sm text-gray-500">
                      Go to the <button type="button" onClick={() => setActiveTab('overview')} className="text-icon hover:underline font-medium">Overview</button> tab to accept the invitation.
                    </p>
                  </div>
                </div>
              ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-ins</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined Club</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contacts.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-sm text-gray-500">
                        No contacts yet. Users will appear here after they opt in and check in at your listing.
                      </td>
                    </tr>
                  ) : (
                    contacts.map((contact) => (
                      <tr key={contact.id}>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {contact.firstName} {contact.lastName}
                          </div>
                          <div className="text-sm text-gray-500">@{contact.username}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {contact.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {contact.checkinCount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {contact.isMemberOfThisListing ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              Yes
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {contact.joinedClubDuringPassport ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Yes
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            type="button"
                            onClick={() => handleToggleMembership(contact)}
                            disabled={togglingMembership === contact.id}
                            className={`px-2 py-1 text-xs font-medium rounded ${
                              contact.joinedClubDuringPassport
                                ? 'text-red-700 bg-red-100 hover:bg-red-200'
                                : 'text-green-700 bg-green-100 hover:bg-green-200'
                            } disabled:opacity-50`}
                          >
                            {togglingMembership === contact.id
                              ? 'Updating...'
                              : contact.joinedClubDuringPassport
                                ? 'Undo'
                                : 'Mark as joined'}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              )}
            </div>
            );
          })()}

          {activeTab === 'analytics' && (
            <PassportAnalyticsPanel passportId={pid} listingId={currentListingId} />
          )}
        </div>
      </div>

      {/* Add Listing Modal */}
      {showAddListingModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowAddListingModal(false)} />

            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Add Listing to Passport</h2>
              <p className="text-sm text-gray-500 mb-4">
                Search for a winery to add to this passport. The listing owner will need to accept the invitation.
              </p>

              <div className="mb-4">
                <label htmlFor="search-listing" className="block text-sm font-medium text-gray-700 mb-1">
                  Search Wineries
                </label>
                <input
                  type="text"
                  id="search-listing"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Type to search..."
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-icon focus:border-icon sm:text-sm"
                />
              </div>

              {searching && (
                <div className="py-4 text-center text-sm text-gray-500">
                  Searching...
                </div>
              )}

              {!searching && searchResults.length > 0 && (
                <ul className="max-h-60 overflow-y-auto divide-y divide-gray-200 border border-gray-200 rounded-md">
                  {searchResults.map((listing) => (
                    <li
                      key={listing.id}
                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                      onClick={() => handleAddListing(listing.id)}
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">{listing.name}</p>
                        {listing.address && (
                          <p className="text-xs text-gray-500">{formatAddressDisplay(listing.address)}</p>
                        )}
                      </div>
                      <PlusIcon className="h-5 w-5 text-gray-400" />
                    </li>
                  ))}
                </ul>
              )}

              {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className="py-4 text-center text-sm text-gray-500">
                  No results found
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddListingModal(false);
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Listing Modal */}
      {showEditListingModal && editingListing && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowEditListingModal(false)} />

            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Edit Listing Details
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                {editingListing.listingName}
              </p>

              <div className="space-y-4">
                <div>
                  <label htmlFor="edit-subtitle" className="block text-sm font-medium text-gray-700 mb-1">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    id="edit-subtitle"
                    value={editFormData.subtitle}
                    onChange={(e) => setEditFormData({ ...editFormData, subtitle: e.target.value })}
                    placeholder="Optional subtitle for this passport"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-icon focus:border-icon sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">A short tagline shown with the listing name</p>
                </div>

                <div>
                  <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="edit-description"
                    rows={4}
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    placeholder="Optional description for this listing in the passport"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-icon focus:border-icon sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">Supports markdown formatting</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Logo
                  </label>
                  {logoPreview ? (
                    <div>
                      <div className="relative inline-block">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="h-24 w-auto object-contain rounded-lg border border-gray-200 cursor-pointer"
                          onClick={() => logoInputRef.current?.click()}
                          title="Click to replace"
                        />
                        {(editingListing?.hasCustomLogo || logoFile) && (
                          <button
                            type="button"
                            onClick={handleRemoveLogo}
                            disabled={updating}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 disabled:opacity-50"
                            title="Remove custom logo"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {!editingListing?.hasCustomLogo && !logoFile && (
                        <p className="mt-1 text-xs text-blue-600">Using logo from listing</p>
                      )}
                    </div>
                  ) : (
                    <div
                      onClick={() => logoInputRef.current?.click()}
                      className="flex items-center justify-center h-24 w-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 hover:bg-gray-50"
                    >
                      <PhotoIcon className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleLogoFileChange}
                    className="hidden"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Click image to upload a custom logo (JPEG, PNG, or WebP)
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditListingModal(false);
                    setEditingListing(null);
                    setLogoFile(null);
                    setLogoPreview(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveListingEdit}
                  disabled={updating || uploadingLogo}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-icon hover:bg-red-700 disabled:opacity-50"
                >
                  {uploadingLogo ? 'Uploading...' : updating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
